/**
 * Local PDF Uploader Component
 * Drag-and-drop upload UI for a drawing PDF — mirrors LocalDXFUploader (csv feature).
 */

import { useState, useRef, DragEvent } from 'react'
import styles from '../../csv/ui/LocalCSVUploader.module.css'

interface LocalPDFUploaderProps {
  onFileSelect: (file: File) => void
  onCancel: () => void
  uploading?: boolean
}

export function LocalPDFUploader({ onFileSelect, onCancel, uploading }: LocalPDFUploaderProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isDragActive, setIsDragActive] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleBrowseClick = () => {
    fileInputRef.current?.click()
  }

  const isValidPDFFile = (file: File): boolean => file.name.toLowerCase().endsWith('.pdf')

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file && isValidPDFFile(file)) {
      setSelectedFile(file)
    }
  }

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragActive(true)
  }

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragActive(false)
  }

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragActive(false)

    const file = e.dataTransfer.files?.[0]
    if (file && isValidPDFFile(file)) {
      setSelectedFile(file)
    }
  }

  const handleUploadClick = () => {
    if (selectedFile) {
      onFileSelect(selectedFile)
    }
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  return (
    <div className={styles.overlay} onClick={onCancel}>
      <div className={styles.container} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>도면 PDF 업로드</h2>
          <p className={styles.description}>PDF 파일을 드래그하거나 클릭해서 찾아보세요</p>
        </div>

        {!selectedFile ? (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,application/pdf"
              onChange={handleFileChange}
              className={styles.hiddenInput}
            />
            <div
              className={`${styles.dropZone} ${isDragActive ? styles.dragActive : ''}`}
              onClick={handleBrowseClick}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <div className={styles.uploadIcon}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
              </div>
              <p className={styles.uploadText}>
                {isDragActive ? '여기에 파일을 놓으세요' : 'PDF 파일 선택'}
              </p>
              <p className={styles.uploadHint}>
                {isDragActive ? '놓으면 업로드됩니다' : '또는 이곳에 드래그 앤 드롭'}
              </p>
            </div>
          </>
        ) : (
          <>
            <div className={styles.selectedFileCard}>
              <div className={styles.fileIconCircle}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <path d="M9 15h6" />
                  <path d="M12 18v-6" />
                </svg>
              </div>
              <div className={styles.fileInfo}>
                <p className={styles.fileName}>{selectedFile.name}</p>
                <div className={styles.fileMeta}>
                  <div className={styles.fileMetaItem}>
                    <span>{formatFileSize(selectedFile.size)}</span>
                  </div>
                </div>
              </div>
              <div className={styles.successCheckmark}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
            </div>
            <button className={styles.changeFileButton} onClick={handleBrowseClick} disabled={uploading}>
              다른 파일 선택
            </button>
          </>
        )}

        <div className={styles.actions}>
          <button className={styles.cancelButton} onClick={onCancel} disabled={uploading}>
            취소
          </button>
          <button
            className={styles.uploadButton}
            onClick={handleUploadClick}
            disabled={!selectedFile || uploading}
          >
            <span>{uploading ? '업로드 중...' : '파일 업로드'}</span>
          </button>
        </div>
      </div>
    </div>
  )
}
